import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Trash2,
  Loader2,
  Volume1,
  Volume2,
  ExternalLink,
  Check,
  X,
  Merge,
  FileText,
  Download,
  Tag as TagIcon,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  ArrowUp,
  MessageSquare,
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import logger from "../../utils/logger";
import IncidentReportModal from "./IncidentReportModal";
import { useAuth } from "../AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const FullscreenMessages = ({
  edgeServerEndpoint,
  messages: messagesProp,
  totalMessages = 0,
  channels,
  showTime,
  showCar,
  showChannel,
  showPerson,
  formatTime,
  timezone,
  timeFormat = "24h",
  highlightText,
  searchQuery,
  setActiveAudioUrl,
  isFullscreen,
  onToggleFullscreen,
  isDarkMode,
  setMessages: setMessagesProp,
  isMobile,
  reverseSort,
  currentPage,
  onLoadMore,
  hasMoreMessages,
  isLoadingMore,
  inboxViewMode = 'continuous', // 'pagination' or 'continuous'
}) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesTopRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // State for caching durations (must be declared before useMemo that uses it)
  const [fetchingDurations, setFetchingDurations] = useState(new Set());
  const [durationsCache, setDurationsCache] = useState({});

  // Removed debug log: console.log("FullscreenMessages rendered", setActiveAudioUrl);
  // Multi-select mode disabled - always false
  const isMultiSelectMode = false;
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  
  // Keep messages in sync and merge in durations from cache
  const messages = useMemo(() => {
    // Merge durations from cache into messages from props
    return messagesProp.map(msg => {
      if (durationsCache[msg.id] && (!msg.duration || msg.duration <= 0)) {
        return { ...msg, duration: durationsCache[msg.id] };
      }
      return msg;
    });
  }, [messagesProp, durationsCache]);
  
  const setMessages = useCallback(
    (newMessages) => setMessagesProp(newMessages),
    [setMessagesProp]
  );

  const { logout, user } = useAuth();
  const { hasPermission } = usePermissions(edgeServerEndpoint);

  // Permission checks
  const canPlayAudio = user?.role === 'admin' || hasPermission('play_audio');
  const canDeleteAudio = user?.role === 'admin' || hasPermission('delete_audio');
  const canAccessAdvancedPlayer = user?.role === 'admin' || hasPermission('access_advanced_player');
  const canCreateReports = user?.role === 'admin' || hasPermission('create_reports');

  // State declarations - moved before useEffect that depends on them
  const [playingAudio, setPlayingAudio] = useState(null);
  const [expandedPlayer, setExpandedPlayer] = useState(null); // Track which message has expanded player
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(new Audio());
  const [iconToggle, setIconToggle] = useState(false);
  const [hallucinations, setHallucinations] = useState([]);
  const [waveformData, setWaveformData] = useState(null); // Store waveform data for current audio
  const audioContextRef = useRef(null);
  const waveformAnimationRef = useRef(null);
  // UI state
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [previousMessageCount, setPreviousMessageCount] = useState(0);
  const [previousFirstMessageId, setPreviousFirstMessageId] = useState(null);
  const [previousLastMessageId, setPreviousLastMessageId] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [hiddenMessages, setHiddenMessages] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [tagsByMessage, setTagsByMessage] = useState({});
  const [checkedMessageIds, setCheckedMessageIds] = useState(new Set());
  const [refreshingTags, setRefreshingTags] = useState(new Set());
  const [fetchedPages, setFetchedPages] = useState(new Set());
  const [selectedTag, setSelectedTag] = useState("");
  const [showTagDropdown, setShowTagDropdown] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isHallucinationEnabled, setIsHallucinationEnabled] = useState(true); // Default to true
  const [expandedMobileActions, setExpandedMobileActions] = useState(null); // Track which message has expanded actions on mobile
  const [showScrollToTop, setShowScrollToTop] = useState(false); // Show floating scroll button on mobile
  const [newMessageCount, setNewMessageCount] = useState(0); // Count of new messages received
  const [lastSeenMessageId, setLastSeenMessageId] = useState(null); // Track last seen message for new count

  // Scroll to newest messages function (handles both sort orientations)
  const scrollToTop = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      if (reverseSort) {
        // Reverse sort: newest messages are at the top, scroll to top
        container.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Normal sort: newest messages are at the bottom, scroll to bottom
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
      setNewMessageCount(0);
      if (messages.length > 0) {
        // Set last seen to the newest message (first in array for reverse sort, last for normal sort)
        const newestMessageId = reverseSort ? messages[0]?.id : messages[messages.length - 1]?.id;
        setLastSeenMessageId(newestMessageId);
      }
    }
  }, [messages, reverseSort]);

  // Reset checked message IDs and page cache when messages change significantly (pagination, filtering, etc.)
  useEffect(() => {
    const currentMessageIds = new Set(messages.map(m => m.id));
    const checkedIds = Array.from(checkedMessageIds);
    
    // If any checked IDs are no longer in current messages, reset the checked set and page cache
    const hasStaleIds = checkedIds.some(id => !currentMessageIds.has(id));
    
    if (hasStaleIds) {
      setCheckedMessageIds(new Set());
      setFetchedPages(new Set()); // Clear page cache when messages change significantly
      // Also clear tags for messages that are no longer present
      setTagsByMessage(prev => {
        const newTags = {};
        Object.keys(prev).forEach(id => {
          if (currentMessageIds.has(parseInt(id))) {
            newTags[id] = prev[id];
          }
        });
        return newTags;
      });
    }
  }, [messages, checkedMessageIds]);

  // Fetch user role from API
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const response = await axios.get(`${edgeServerEndpoint}/users/${user.username}`);
        setUserRole(response.data[user.username]?.role || 'member');
      } catch (error) {
        logger.error('Error fetching user role:', error);
        setUserRole('member'); // Fallback to 'member' if API call fails
      }
    };
    fetchUserRole();
  }, [user.username, edgeServerEndpoint]);

  // Fetch duration for messages that don't have it
  useEffect(() => {
    const fetchMissingDurations = async () => {
      // Find messages without duration that we haven't already fetched
      const messagesNeedingDuration = messages.filter(
        msg => msg.id && 
        (!msg.duration || msg.duration <= 0) && 
        !fetchingDurations.has(msg.id) &&
        !durationsCache[msg.id]
      );

      if (messagesNeedingDuration.length === 0) return;

      // Limit to first 10 to avoid too many simultaneous requests
      const messagesToFetch = messagesNeedingDuration.slice(0, 10);
      
      messagesToFetch.forEach(async (msg) => {
        setFetchingDurations(prev => new Set([...prev, msg.id]));
        
        try {
          const res = await axios.get(`${edgeServerEndpoint}/recording_duration_calculate/${msg.id}`);
          if (res.data && res.data.duration_seconds) {
            const duration = res.data.duration_seconds;
            // Cache the duration - it will be merged into messages via useMemo
            setDurationsCache(prev => ({ ...prev, [msg.id]: duration }));
            
            // Also update the backend database by calling the endpoint (it stores it)
            // The duration is already stored by the endpoint, so we don't need to do anything else
          }
        } catch (error) {
          logger.error(`Failed to fetch duration for message ${msg.id}:`, error);
        } finally {
          setFetchingDurations(prev => {
            const newSet = new Set(prev);
            newSet.delete(msg.id);
            return newSet;
          });
        }
      });
    };

    // Debounce to avoid too frequent calls
    const timeoutId = setTimeout(fetchMissingDurations, 500);
    return () => clearTimeout(timeoutId);
  }, [messages, edgeServerEndpoint, fetchingDurations, durationsCache, setMessages]);

  // Available tags and fetched tags

  // Toast wrapper
  const TOAST_CONFIG = {
    position: isMobile ? "bottom-center" : "top-right",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    theme: isDarkMode ? "dark" : "light",
    style: {
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
      minWidth: "300px",
    },
    progressStyle: {
      background: isDarkMode ? "#4B5563" : "#D1D5DB",
    },
  };
  const showToast = useCallback(
    (message, type = "info", customOptions = {}) =>
      toast(
        <div className="flex items-center justify-between w-full">
          <span className="text-sm font-medium">{message}</span>
          {customOptions.undo && (
            <button
              onClick={customOptions.undo.action}
              className="ml-4 px-2 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Undo
            </button>
          )}
        </div>,
        { ...TOAST_CONFIG, type, ...customOptions }
      ),
    [isDarkMode, isMobile]
  );

  // Function to replace hallucination words with "....." in the text
  const replaceHallucinationWords = (text, hallucinations) => {
    // Safety check: ensure hallucinations is an array
    if (!Array.isArray(hallucinations) || hallucinations.length === 0) {
      return text;
    }

    let filteredText = text;

    hallucinations.forEach(({ text: pattern, type }) => {
      try {
        let regexPattern;
        
        if (type.includes('regex')) {
          // Check if pattern contains regex special characters
          const hasRegexSpecialChars = /[.+*?^${}()|[\]\\]/.test(pattern);
          
          if (hasRegexSpecialChars) {
            // User provided a regex pattern, use it as-is
            regexPattern = pattern;
          } else {
            // Plain word - escape it and match anywhere (not just whole words)
            // This allows matching "This" in "This is a test" or "somethingThis"
            const escapedPattern = pattern.replace(/[.+*?^${}()|[\]\\]/g, '\\$&');
            regexPattern = escapedPattern;
          }
        } else if (type.includes('wildcard')) {
          // Convert wildcard to regex
          const hasWildcardChars = pattern.includes('*') || pattern.includes('?');
          let escapedPattern = pattern
            .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special regex chars
            .replace(/\*/g, '.*') // Convert * to .*
            .replace(/\?/g, '.'); // Convert ? to .
          
          if (hasWildcardChars) {
            regexPattern = escapedPattern;
          } else {
            // Plain word with wildcard type - match anywhere
            regexPattern = escapedPattern;
          }
        } else {
          // Unknown type, skip
          return;
        }
        
        const regex = new RegExp(regexPattern, 'gi'); // Global, case-insensitive
        filteredText = filteredText.replace(regex, '.....');
      } catch (err) {
        logger.error(`Error processing hallucination pattern "${pattern}":`, err);
      }
    });

    return filteredText;
  };

  const checkPatternMatches = (text, hallucinations) => {
  const matches = { regex: false, wildcard: false };

  // Safety check: ensure hallucinations is an array
  if (!Array.isArray(hallucinations) || hallucinations.length === 0) {
    return matches;
  }

  hallucinations.forEach(({ text: pattern, type }) => {
    if (type.includes('regex')) {
      try {
        // Check if pattern contains regex special characters
        // If it's a plain word, escape it and search anywhere in text
        // If it contains regex chars, use it as-is (user knows what they're doing)
        const hasRegexSpecialChars = /[.+*?^${}()|[\]\\]/.test(pattern);
        let regexPattern;
        
        if (hasRegexSpecialChars) {
          // User provided a regex pattern, use it as-is
          regexPattern = pattern;
        } else {
          // Plain word - escape it and search anywhere in text
          const escapedPattern = pattern.replace(/[.+*?^${}()|[\]\\]/g, '\\$&');
          regexPattern = escapedPattern;
        }
        
        const regex = new RegExp(regexPattern, 'i'); // Case-insensitive regex
        if (regex.test(text)) {
          matches.regex = true;
        }
      } catch (err) {
        logger.error(`Invalid regex pattern: ${pattern}`, err);
      }
    }
    if (type.includes('wildcard')) {
      // Convert wildcard to regex (e.g., *test* -> .*test.*)
      // If pattern doesn't contain * or ?, treat it as a word that can appear anywhere
      const hasWildcardChars = pattern.includes('*') || pattern.includes('?');
      let escapedPattern = pattern
        .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special regex chars
        .replace(/\*/g, '.*') // Convert * to .*
        .replace(/\?/g, '.'); // Convert ? to .
      
      try {
        // If no wildcard chars, check if pattern appears anywhere in text
        // Otherwise, check if entire text matches the pattern
        const wildcardRegex = hasWildcardChars 
          ? new RegExp(`^${escapedPattern}$`, 'i') // Case-insensitive, full match
          : new RegExp(escapedPattern, 'i'); // Case-insensitive, anywhere in text
        if (wildcardRegex.test(text)) {
          matches.wildcard = true;
        }
      } catch (err) {
        logger.error(`Invalid wildcard pattern: ${pattern}`, err);
      }
    }
  });

  return matches;
};

  const handlePlayAudio = async (url, messageId) => {
    // If this is the currently loaded audio
    if (playingAudio === url) {
      if (isPlaying) {
        // Pause the audio
        if (audioRef.current) {
          audioRef.current.pause();
        }
        setIsPlaying(false);
      } else {
        // Resume playing
        try {
          if (audioRef.current) {
            await audioRef.current.play();
            setIsPlaying(true);
          }
        } catch (error) {
          logger.error('Error playing audio:', error);
          setIsPlaying(false);
        }
      }
    } else {
      // Switch to a different audio file
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current.src = url;
      audioRef.current.load(); // Load the audio first
      try {
        await audioRef.current.play();
        setPlayingAudio(url);
        setIsPlaying(true);
        setExpandedPlayer(messageId); // Expand the player for this message
      } catch (error) {
        logger.error('Error playing audio:', error);
        setIsPlaying(false);
      }
    }
  };

  const togglePlayerExpand = (messageId, url) => {
    if (expandedPlayer === messageId) {
      // Collapse player and stop audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setExpandedPlayer(null);
      setPlayingAudio(null);
      setIsPlaying(false);
      setCurrentTime(0);
    } else {
      // Expand player for this message and auto-play
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current.src = url;
      audioRef.current.load();
      setPlayingAudio(url);
      setExpandedPlayer(messageId);
      setCurrentTime(0);
      // Auto-play the audio when player expands
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch((error) => {
        logger.error('Error auto-playing audio:', error);
        setIsPlaying(false);
      });
    }
  };

  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value);
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const skipTime = (seconds) => {
    const newTime = Math.max(0, Math.min(audioRef.current.currentTime + seconds, duration));
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatAudioTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Parse timestamp from YYYYMMDD_HHMMSS format
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    // Handle YYYYMMDD_HHMMSS format
    if (/^\d{8}_\d{6}$/.test(timestamp)) {
      const year = parseInt(timestamp.substring(0, 4));
      const month = parseInt(timestamp.substring(4, 6)) - 1; // Month is 0-indexed
      const day = parseInt(timestamp.substring(6, 8));
      const hours = parseInt(timestamp.substring(9, 11));
      const minutes = parseInt(timestamp.substring(11, 13));
      const seconds = parseInt(timestamp.substring(13, 15));
      return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    }
    
    // Try to parse as ISO string or other formats
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  };

  // Format timestamp with milliseconds: HH:MM:SS:Millis (respects 12h/24h format)
  const formatTimestampWithMillis = (timestamp, playbackOffset = 0) => {
    if (!timestamp) return timeFormat === '12h' ? '00:00:00:000 AM' : '00:00:00:000';
    
    try {
      const startDate = parseTimestamp(timestamp);
      if (!startDate) return timeFormat === '12h' ? '00:00:00:000 AM' : '00:00:00:000';
      
      // Add playback offset in milliseconds
      const actualTime = new Date(startDate.getTime() + playbackOffset * 1000);
      
      // Get time components in local browser timezone
      const hours24 = actualTime.getHours();
      const minutes = String(actualTime.getMinutes()).padStart(2, '0');
      const seconds = String(actualTime.getSeconds()).padStart(2, '0');
      const milliseconds = String(actualTime.getMilliseconds()).padStart(3, '0');
      
      if (timeFormat === '12h') {
        // 12-hour format: HH:MM:SS:Millis AM/PM
        const hour12 = hours24 % 12 || 12;
        const ampm = hours24 >= 12 ? 'PM' : 'AM';
        return `${String(hour12).padStart(2, '0')}:${minutes}:${seconds}:${milliseconds} ${ampm}`;
      } else {
        // 24-hour format: HH:MM:SS:Millis
        const hours = String(hours24).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}:${milliseconds}`;
      }
    } catch (error) {
      logger.error('Error formatting timestamp:', error);
      return timeFormat === '12h' ? '00:00:00:000 AM' : '00:00:00:000';
    }
  };

  // Generate waveform data from audio
  const generateWaveform = useCallback(async (audioUrl) => {
    try {
      if (!audioUrl) {
        setWaveformData(null);
        return;
      }

      // Create audio context if it doesn't exist
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

      // Get channel data (use first channel)
      const channelData = audioBuffer.getChannelData(0);
      const samples = 200; // Number of waveform bars
      const blockSize = Math.floor(channelData.length / samples);
      const waveform = [];

      // Sample the audio data
      for (let i = 0; i < samples; i++) {
        let sum = 0;
        const start = i * blockSize;
        const end = Math.min(start + blockSize, channelData.length);
        
        for (let j = start; j < end; j++) {
          sum += Math.abs(channelData[j]);
        }
        
        const average = sum / (end - start);
        waveform.push(average);
      }

      // Normalize waveform data
      const max = Math.max(...waveform);
      const normalizedWaveform = waveform.map(value => max > 0 ? value / max : 0);
      
      setWaveformData(normalizedWaveform);
    } catch (error) {
      logger.error('Error generating waveform:', error);
      setWaveformData(null);
    }
  }, []);

  // Generate waveform when audio URL changes
  useEffect(() => {
    if (playingAudio) {
      generateWaveform(playingAudio);
    } else {
      setWaveformData(null);
    }
  }, [playingAudio, generateWaveform]);

  // Handle audio ended and time updates
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Clear audio playing flag when audio ends
      localStorage.removeItem('audioIsPlaying');
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleDurationChange = () => {
      setDuration(audio.duration);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      localStorage.setItem('audioIsPlaying', 'true');
    };

    const handlePause = () => {
      setIsPlaying(false);
      localStorage.removeItem('audioIsPlaying');
    };

    const handleError = (e) => {
      logger.error('Audio error:', e);
      setIsPlaying(false);
      localStorage.removeItem('audioIsPlaying');
    };

    // Add event listeners
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    return () => {
      // Remove event listeners
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      // Clear audio playing flag on cleanup
      localStorage.removeItem('audioIsPlaying');
    };
  }, []);

  // Track audio playing state in localStorage to pause screen refreshes
  useEffect(() => {
    if (isPlaying) {
      localStorage.setItem('audioIsPlaying', 'true');
    } else {
      localStorage.removeItem('audioIsPlaying');
    }
  }, [isPlaying]);

  // Icon animation effect
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setIconToggle(prev => !prev);
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  // Infinite scroll for all devices
  useEffect(() => {
    if (!onLoadMore) return;
    
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Load more when user scrolls near the bottom (within 200px)
      const nearBottom = scrollHeight - scrollTop - clientHeight < 200;
      
      if (nearBottom && hasMoreMessages && !isLoadingMore) {
        onLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [onLoadMore, hasMoreMessages, isLoadingMore]);

  // Auto-scroll logic and scroll-to-newest button visibility
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      if (reverseSort) {
        // For reverse sorting, we check if we're at the top (newest messages)
        setIsAtBottom(scrollTop < 10);
        // Show button when scrolled down from top
        setShowScrollToTop(scrollTop > 200);
        // Reset new message count when at top (newest messages)
        if (scrollTop < 50) {
          setNewMessageCount(0);
          if (messages.length > 0) {
            setLastSeenMessageId(messages[0]?.id);
          }
        }
      } else {
        // For normal sorting, we check if we're at the bottom (newest messages)
        setIsAtBottom(distanceFromBottom < 10);
        // Show button when scrolled up from bottom
        setShowScrollToTop(distanceFromBottom > 200);
        // Reset new message count when at bottom (newest messages)
        if (distanceFromBottom < 50) {
          setNewMessageCount(0);
          if (messages.length > 0) {
            setLastSeenMessageId(messages[messages.length - 1]?.id);
          }
        }
      }
    };
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [reverseSort, messages]);

  // Track new messages on mobile
  useEffect(() => {
    if (!isMobile || !messages.length) return;
    
    // Initialize last seen message ID
    if (!lastSeenMessageId && messages.length > 0) {
      setLastSeenMessageId(messages[0]?.id);
      return;
    }
    
    // Count new messages since last seen
    if (lastSeenMessageId && showScrollToTop) {
      const lastSeenIndex = messages.findIndex(m => m.id === lastSeenMessageId);
      if (lastSeenIndex > 0) {
        setNewMessageCount(lastSeenIndex);
      }
    }
  }, [messages, isMobile, lastSeenMessageId, showScrollToTop]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (isInitialLoad && messages.length > 0) {
      // On initial load, scroll to appropriate position based on sort order
      if (reverseSort) {
        // Scroll to top (newest messages)
        container.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        // Scroll to bottom (newest messages)
        container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      }
      setIsInitialLoad(false);
      setPreviousMessageCount(messages.length);
      setPreviousFirstMessageId(messages[0]?.id || null);
      setPreviousLastMessageId(messages[messages.length - 1]?.id || null);
    } else if (messages.length > 0) {
      // Check if new messages were actually added (not just filtered/reordered)
      const hasNewMessages = messages.length > previousMessageCount;
      const firstMessageChanged = reverseSort && messages[0]?.id !== previousFirstMessageId;
      const lastMessageChanged = !reverseSort && messages[messages.length - 1]?.id !== previousLastMessageId;
      
      // Only auto-scroll if:
      // 1. User is at the newest messages position (isAtBottom)
      // 2. New messages were actually added (not just a filter change)
      if (isAtBottom && (hasNewMessages || firstMessageChanged || lastMessageChanged)) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          if (reverseSort) {
            // For reverse sort: new messages appear at top, maintain scroll at top
            container.scrollTo({ top: 0, behavior: "auto" });
          } else {
            // For normal sort: new messages appear at bottom, maintain scroll at bottom
            container.scrollTo({ top: container.scrollHeight, behavior: "auto" });
          }
        });
      }
      
      // Update tracking state
      setPreviousMessageCount(messages.length);
      setPreviousFirstMessageId(messages[0]?.id || null);
      setPreviousLastMessageId(messages[messages.length - 1]?.id || null);
    }
  }, [messages, isAtBottom, reverseSort, isInitialLoad, previousMessageCount, previousFirstMessageId, previousLastMessageId]);

  // Reset isInitialLoad when currentPage changes to trigger auto-scroll for new page
  useEffect(() => {
    setIsInitialLoad(true);
  }, [currentPage]);

  // Fetch all available tags
  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${edgeServerEndpoint}/tags`);
        setAllTags(resp.data.map((t) => t.name));
      } catch (err) {
        logger.error("Failed to load tags:", err);
        showToast("Failed to load available tags", "error");
      }
    })();
  }, [edgeServerEndpoint, showToast]);

  // Fetch global settings to check if hallucination filtering is enabled
  useEffect(() => {
    const fetchGlobalSettings = async () => {
      try {
        const response = await axios.get(`${edgeServerEndpoint}/settings`);
        const hallucinationSetting = response.data?.global_hallucination || "True";
        setIsHallucinationEnabled(hallucinationSetting === "True");
      } catch (err) {
        logger.error('Error fetching global settings:', err);
        // Default to enabled if fetch fails
        setIsHallucinationEnabled(true);
      }
    };
    fetchGlobalSettings();
  }, [edgeServerEndpoint]);

   useEffect(() => {
      const fetchHallucinations = async () => {
        // Only fetch hallucinations if the feature is enabled
        if (!isHallucinationEnabled) {
          setHallucinations([]);
          return;
        }

        try {
          // Match the pattern used for tags endpoint: ${edgeServerEndpoint}/tags
          // The route is /api/hallucinations, so if edgeServerEndpoint includes /api, 
          // this will work. If not, we'll get an error and can debug.
          const response = await fetch(`${edgeServerEndpoint}/hallucinations`);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch hallucinations: ${response.status} ${response.statusText}`);
          }
          
          const contentType = response.headers.get('content-type') || '';
          if (!contentType.includes('application/json')) {
            const text = await response.text();
            logger.error('Non-JSON response (first 200 chars):', text.substring(0, 200));
            throw new Error('Server returned non-JSON response. Check if endpoint is correct.');
          }
          
          const data = await response.json();
          logger.debug('Fetched hallucinations:', data);
          setHallucinations(data);
        } catch (err) {
          logger.error('Error fetching hallucinations:', err);
          // setError('Failed to load hallucinations');
        }
      };
      fetchHallucinations();
    }, [edgeServerEndpoint, isHallucinationEnabled]);
  // Fetch tags for messages using batch endpoint with page-based caching
  useEffect(() => {
    const fetchTagsBatch = async () => {
      try {
        // Check if we've already fetched tags for this page
        if (fetchedPages.has(currentPage)) {
          return; // Already fetched for this page
        }

        // Filter out messages that have already been checked
        const uncheckedMessages = messages.filter(message => !checkedMessageIds.has(message.id));
        
        if (uncheckedMessages.length === 0) {
          // Mark page as fetched even if no messages to check
          setFetchedPages(prev => new Set(prev).add(currentPage));
          return;
        }

        // Use batch endpoint to fetch tags for all unchecked messages at once
        const recordingIds = uncheckedMessages.map(message => message.id);
        
        const response = await axios.post(
          `${edgeServerEndpoint}/recordings_tag/batch/tags`,
          { recording_ids: recordingIds }
        );
        
        const batchTags = response.data;
        const newTagsByMessage = { ...tagsByMessage };
        const newCheckedIds = new Set(checkedMessageIds);
        
        // Update tags for all messages in the batch
        uncheckedMessages.forEach(message => {
          newTagsByMessage[message.id] = batchTags[message.id] || [];
          newCheckedIds.add(message.id);
        });
        
        setTagsByMessage(newTagsByMessage);
        setCheckedMessageIds(newCheckedIds);
        
        // Mark this page as fetched
        setFetchedPages(prev => new Set(prev).add(currentPage));
      } catch (err) {
        logger.error("Error fetching tags for messages:", err);
        showToast("Failed to load message tags", "error");
      }
    };

    if (messages.length > 0) {
      fetchTagsBatch();
    }
  }, [messages, edgeServerEndpoint, showToast, checkedMessageIds, tagsByMessage, currentPage]);



  // Multi-select functionality removed - these functions are no-op
  const toggleMessageSelection = () => {}; // Disabled
  const selectAllMessages = () => {}; // Disabled
  const clearSelection = () => {}; // Disabled

  // Tag management with API
  const addTagToMessage = async (messageId, tag) => {
    if (!tag || tagsByMessage[messageId]?.includes(tag)) return;
    
    // Set refreshing state to show loading indicator
    setRefreshingTags(prev => new Set(prev).add(messageId));
    
    try {
      await axios.post(`${edgeServerEndpoint}/recordings_tag/${messageId}/tags`, {
        tag,
      });
      setTagsByMessage((prev) => ({
        ...prev,
        [messageId]: [...(prev[messageId] || []), tag],
      }));
      // Remove from checked IDs to allow re-fetching if needed
      setCheckedMessageIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
      // Success notifications removed - only show errors
    } catch (err) {
      logger.error(`Failed to add tag ${tag} to message ${messageId}:`, err);
      showToast(`Failed to add tag "${tag}"`, "error");
    } finally {
      // Clear refreshing state
      setRefreshingTags(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
    setSelectedTag("");
    setShowTagDropdown(null);
  };

  const addTagToSelectedMessages = async () => {
    if (selectedMessages.size === 0 || !selectedTag) {
      return showToast("Please select messages and a tag", "warning");
    }
    setIsProcessing(true);
    try {
      await Promise.all(
        Array.from(selectedMessages).map(async (id) => {
          if (!tagsByMessage[id]?.includes(selectedTag)) {
            await axios.post(`${edgeServerEndpoint}/recordings_tag/${id}/tags`, {
              tag: selectedTag,
            });
          }
        })
      );
      setTagsByMessage((prev) => {
        const updated = { ...prev };
        selectedMessages.forEach((id) => {
          if (!updated[id]?.includes(selectedTag)) {
            updated[id] = [...(updated[id] || []), selectedTag];
          }
        });
        return updated;
      });
      showToast(
        `Tag “${selectedTag}” added to ${selectedMessages.size} messages`,
        "success"
      );
    } catch (err) {
      logger.error("Failed to add tags to selected messages:", err);
      showToast("Failed to add tag to selected messages", "error");
    } finally {
      setIsProcessing(false);
      setSelectedTag("");
      clearSelection();
    }
  };

  const removeTagFromMessage = async (messageId, tag) => {
    // Set refreshing state to show loading indicator
    setRefreshingTags(prev => new Set(prev).add(messageId));
    
    try {
      await axios.delete(
        `${edgeServerEndpoint}/recordings_tag/${messageId}/tags/${tag}`
      );
      setTagsByMessage((prev) => ({
        ...prev,
        [messageId]: prev[messageId].filter((t) => t !== tag),
      }));
      // Success notifications removed - only show errors
    } catch (err) {
      logger.error(`Failed to remove tag ${tag} from message ${messageId}:`, err);
      showToast(`Failed to remove tag "${tag}"`, "error");
    } finally {
      // Clear refreshing state
      setRefreshingTags(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  // Clear all tag cache and re-fetch
  const clearTagCache = () => {
    setCheckedMessageIds(new Set());
    setFetchedPages(new Set());
    setTagsByMessage({});
    setRefreshingTags(new Set()); // Clear any stuck refreshing states
  };

  // Clear any stuck refreshing states
  const clearRefreshingStates = () => {
    setRefreshingTags(new Set());
  };

  // Add timeout to clear refreshing states after 10 seconds
  useEffect(() => {
    if (refreshingTags.size > 0) {
      const timeout = setTimeout(() => {
        logger.warn('Clearing stuck refreshing states after timeout');
        setRefreshingTags(new Set());
      }, 10000); // 10 seconds timeout

      return () => clearTimeout(timeout);
    }
  }, [refreshingTags]);

  // Manual refresh tags for a specific message
  const refreshTagsForMessage = async (messageId) => {
    try {
      // Set refreshing state
      setRefreshingTags(prev => new Set(prev).add(messageId));
      
      // Remove from checked IDs to force re-fetch
      setCheckedMessageIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
      
      // Fetch tags for this specific message
      const response = await axios.get(
        `${edgeServerEndpoint}/recordings_tag/${messageId}/tags`
      );
      
      setTagsByMessage((prev) => ({
        ...prev,
        [messageId]: response.data,
      }));
      
      // Mark as checked
      setCheckedMessageIds(prev => {
        const newSet = new Set(prev);
        newSet.add(messageId);
        return newSet;
      });
      
      // Success notifications removed - only show errors
    } catch (err) {
      logger.error(`Failed to refresh tags for message ${messageId}:`, err);
      showToast("Failed to refresh tags", "error");
    } finally {
      // Clear refreshing state
      setRefreshingTags(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  // Merge logic
  const mergeSelectedMessages = async () => {
    if (selectedMessages.size < 2) {
      return showToast("Please select at least 2 messages to merge", "warning");
    }
    setIsProcessing(true);
    try {
      const toMerge = messages
        .filter((m) => selectedMessages.has(m.id))
        .sort((a, b) => new Date(a.time) - new Date(b.time));
      const mergedText = toMerge.map((m) => m.message).join(" ");
      const base = toMerge[0];
      const newMsg = {
        id: `merged_${Date.now()}`,
        time: base.time,
        message: mergedText,
        channel: base.channel,
        status: "merged",
      };
      // Replace in state
      setMessages((prev) => {
        const remaining = prev.filter((m) => !selectedMessages.has(m.id));
        return [...remaining, newMsg].sort(
          (a, b) => new Date(a.time) - new Date(b.time)
        );
      });
      // Merge tags
      const mergedTags = [
        ...new Set(toMerge.flatMap((m) => tagsByMessage[m.id] || [])),
      ];
      if (mergedTags.length) {
        try {
          await Promise.all(
            mergedTags.map((tag) =>
              axios.post(`${edgeServerEndpoint}/recordings_tag/${newMsg.id}/tags`, {
                tag,
              })
            )
          );
          setTagsByMessage((prev) => ({
            ...prev,
            [newMsg.id]: mergedTags,
          }));
        } catch (err) {
          logger.error("Failed to add merged tags:", err);
          showToast("Failed to add tags to merged message", "error");
        }
      }
      // Success notifications removed - only show errors
      clearSelection();
    } catch (err) {
      logger.error("Failed to merge messages:", err);
      showToast("Failed to merge messages", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  // Incident report
  const createIncidentReport = () => {
    if (selectedMessages.size === 0) {
      return showToast(
        "Please select messages to include in the incident report",
        "warning"
      );
    }
    // Check if any selected messages are in the current filtered messages
    const visibleSelectedMessages = messages.filter((msg) => selectedMessages.has(msg.id));
    if (visibleSelectedMessages.length === 0) {
      return showToast(
        "No selected messages are visible in the current filter. Please select messages from the visible list.",
        "warning"
      );
    }
    // Warn if some selected messages are not visible
    if (visibleSelectedMessages.length < selectedMessages.size) {
      showToast(
        `${visibleSelectedMessages.length} of ${selectedMessages.size} selected messages are visible. Only visible messages will be included.`,
        "info"
      );
    }
    setShowIncidentModal(true);
  };
  const handleIncidentSubmit = async (reportData) => {
    setIsProcessing(true);
    try {
      const { data } = await axios.post(
        `${edgeServerEndpoint}/incident-reports`,
        reportData
      );
      // Success notifications removed - only show errors
      clearSelection();
      setShowIncidentModal(false);
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || "Failed to submit report",
        "error"
      );
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete helpers
  const deleteSelectedMessages = async () => {
    if (selectedMessages.size === 0) {
      return showToast("Please select messages to delete", "warning");
    }
    if (!window.confirm(`Delete ${selectedMessages.size} messages?`)) return;
    setIsProcessing(true);
    try {
      await Promise.all(
        Array.from(selectedMessages).map((id) =>
          axios.delete(`${edgeServerEndpoint}/recordings/${id}`)
        )
      );
      setMessages((prev) => prev.filter((m) => !selectedMessages.has(m.id)));
      setTagsByMessage((prev) => {
        const upd = { ...prev };
        selectedMessages.forEach((id) => delete upd[id]);
        return upd;
      });
      // Success notifications removed - only show errors
      clearSelection();
    } catch (err) {
      logger.error("Failed to delete messages:", err);
      showToast("Failed to delete some messages", "error");
    } finally {
      setIsProcessing(false);
    }
  };
  const deleteMessage = async (id) => {
    setHiddenMessages((prev) => new Set(prev).add(id));
    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      // Delete immediately without success notification
      await axios.delete(`${edgeServerEndpoint}/recordings/${id}`);
      setMessages((p) => p.filter((m) => m.id !== id));
      setTagsByMessage((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
      // Remove from hidden/deleting sets after successful delete
      setHiddenMessages((p) => {
        const n = new Set(p);
        n.delete(id);
        return n;
      });
      setDeletingIds((p) => {
        const n = new Set(p);
        n.delete(id);
        return n;
      });
    } catch (err) {
      logger.error("Failed to delete message:", err);
      showToast("Failed to delete message", "error");
      // Restore message on error
      setHiddenMessages((p) => {
        const n = new Set(p);
        n.delete(id);
        return n;
      });
      setDeletingIds((p) => {
        const n = new Set(p);
        n.delete(id);
        return n;
      });
    }
  };

  // Navigation helper
  const navigateToAdvancedPlayer = (url, messageId) =>
    navigate(
      `/advanced-player?messageId=${messageId}`
    );

// const AudioIcon = ({ url, messageId, isDarkMode = false, isMobile = false }) => {
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [audio] = useState(new Audio(url)); // Create Audio instance

//   // Toggle play/pause for the audio
//   const handlePlayClick = (e) => {
//     e.stopPropagation();

//     if (isPlaying) {
//       audio.pause();
//       setIsPlaying(false);
//     } else {
//       audio.play().catch((err) => console.error("Audio playback error:", err));
//       setIsPlaying(true);
//     }
//   };

//   // Navigate to advanced player (keeping your original logic)
//   const navigateToAdvancedPlayer = (url, messageId) => {
//     // console.log("Navigating to advanced player:", { url, messageId });
//    navigate(
//       `/advanced-player?audioUrl=${encodeURIComponent(url)}&messageId=${messageId}`
//     );

//   };

//   // Ensure audio stops when component unmounts
//   useEffect(() => {
//     return () => {
//       audio.pause();
//       audio.currentTime = 0;
//     };
//   }, [audio]);

//   return (
//     <div className="inline-flex items-center">
//       <span
//         onClick={handlePlayClick}
//         className="cursor-pointer"
//         title={isPlaying ? "Pause" : "Play mini"}
//       >
//         <Volume1
//           className={`inline ${
//             isDarkMode ? "text-gray-400 hover:text-blue-400" : "text-gray-500 hover:text-blue-600"
//           } ${isPlaying ? "text-blue-600" : ""}`}
//           size={isMobile ? 16 : 18}
//         />
//       </span>
//       <span
//         onClick={(e) => {
//           e.stopPropagation();
//           navigateToAdvancedPlayer(url, messageId);
//         }}
//         className={`ml-2 ${isMobile ? "text-[10px]" : "text-xs"} ${
//           isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"
//         } hover:underline cursor-pointer`}
//         title="Open advanced"
//       >
//         <ExternalLink size={isMobile ? 12 : 14} className="inline mr-1" />
//         Adv
//       </span>
//    </div>
//   );
// };
  const AudioIcon = ({ url, messageId }) => {
    const isCurrentlyPlaying = playingAudio === url && isPlaying;
    const isExpanded = expandedPlayer === messageId;
    return (
      <div 
        className={`inline-flex items-center cursor-pointer transition-colors ${
          isExpanded ? 'text-blue-500' : isCurrentlyPlaying ? 'text-blue-500' : 'text-gray-400 hover:text-blue-400'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          togglePlayerExpand(messageId, url);
        }}
        title={isExpanded ? "Close player" : "Open player"}
      >
        {isCurrentlyPlaying ? (
          iconToggle ? (
            <Volume2 className="ml-2" size={18} />
          ) : (
            <Volume1 className="ml-2" size={18} />
          )
        ) : (
          <Volume1 className="ml-2" size={18} />
        )}
      </div>
    );
  };

  // Format duration as MM:SS
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Duration Card Component - shows audio duration at end of transcription
  const DurationCard = ({ duration: messageDuration }) => {
    // Use duration from message object if available
    if (!messageDuration || messageDuration <= 0) {
      return null;
    }

    return (
      <span className="inline-block ml-2 text-xs font-mono text-blue-400">
        {formatDuration(messageDuration)}
      </span>
    );
  };

  // Inline Audio Player Component - Full width below transcription
  const InlineAudioPlayer = ({ url, messageId }) => {
    const isThisPlaying = playingAudio === url && isPlaying;
    const progressPercent = duration ? ((playingAudio === url ? currentTime : 0) / duration) * 100 : 0;
    const localWaveformCanvasRef = useRef(null);
    
    // Find the message to get its timestamp
    const message = messages.find(m => m.id === messageId);
    const playbackTime = playingAudio === url ? currentTime : 0;
    const actualTimestamp = message ? formatTimestampWithMillis(message.time, playbackTime) : '00:00:00:000';
    
    // Draw waveform on canvas for this specific player
    useEffect(() => {
      if (!localWaveformCanvasRef.current || !waveformData || waveformData.length === 0 || playingAudio !== url) {
        // Cancel animation if conditions not met
        if (waveformAnimationRef.current) {
          cancelAnimationFrame(waveformAnimationRef.current);
          waveformAnimationRef.current = null;
        }
        return;
      }

      const canvas = localWaveformCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const container = canvas.parentElement;
      
      if (!container) return;

      // Set canvas size to match container (only once, not on every update)
      const rect = container.getBoundingClientRect();
      if (canvas.width !== rect.width || canvas.height !== 32) {
        canvas.width = rect.width;
        canvas.height = 32;
      }

      const width = canvas.width;
      const height = canvas.height;
      const barCount = waveformData.length;
      const barWidth = width / barCount;
      const centerY = height / 2;

      // Cache colors
      const playedColor = isDarkMode ? '#60a5fa' : '#2563eb';
      const unplayedColor = isDarkMode ? '#4b5563' : '#9ca3af';

      // Create offscreen canvas for base waveform (drawn once, never cleared)
      const baseCanvas = document.createElement('canvas');
      baseCanvas.width = width;
      baseCanvas.height = height;
      const baseCtx = baseCanvas.getContext('2d');
      
      // Draw base waveform once on offscreen canvas
      waveformData.forEach((value, index) => {
        const x = index * barWidth;
        const barHeight = value * (height * 0.8);
        baseCtx.fillStyle = unplayedColor;
        baseCtx.fillRect(x, centerY - barHeight / 2, barWidth - 1, barHeight);
      });

      // Create offscreen canvas for progress layer (double buffering)
      const progressCanvas = document.createElement('canvas');
      progressCanvas.width = width;
      progressCanvas.height = height;
      const progressCtx = progressCanvas.getContext('2d');

      // Draw base waveform to main canvas once
      ctx.drawImage(baseCanvas, 0, 0);

      // Track last drawn progress to only update changed bars
      let lastDrawnBarIndex = -1;
      let lastUpdateTime = 0;
      const UPDATE_INTERVAL = 100; // Update every 100ms (~10fps) to significantly reduce flickering

      // Draw full waveform with progress using double buffering
      const drawWaveformWithProgress = (progressTime) => {
        if (!duration) {
          // Just draw base waveform
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(baseCanvas, 0, 0);
          return;
        }
        
        const currentProgressPercent = (progressTime / duration) * 100;
        const progressPosition = currentProgressPercent / 100 * width;
        const currentBarIndex = Math.floor((progressPosition / width) * barCount);
        
        // Only redraw if we've moved to a new bar (throttle updates)
        if (currentBarIndex !== lastDrawnBarIndex) {
          // Clear and redraw progress layer on offscreen canvas
          progressCtx.clearRect(0, 0, width, height);
          
          // Draw played portion on progress canvas
          progressCtx.fillStyle = playedColor;
          for (let i = 0; i <= currentBarIndex && i < barCount; i++) {
            const x = i * barWidth;
            const barHeight = waveformData[i] * (height * 0.8);
            progressCtx.fillRect(x, centerY - barHeight / 2, barWidth - 1, barHeight);
          }
          
          // Composite both canvases to main canvas in one operation (no flicker)
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(baseCanvas, 0, 0);
          ctx.drawImage(progressCanvas, 0, 0);
          
          lastDrawnBarIndex = currentBarIndex;
        }
      };

      // Initial draw
      drawWaveformWithProgress(isThisPlaying && duration ? currentTime : 0);

      // Animation loop - throttled to reduce flickering
      const animate = (timestamp) => {
        if (isThisPlaying && playingAudio === url && duration && audioRef.current) {
          // Throttle updates to reduce flickering
          if (timestamp - lastUpdateTime >= UPDATE_INTERVAL) {
            const currentProgressTime = audioRef.current.currentTime || 0;
            drawWaveformWithProgress(currentProgressTime);
            lastUpdateTime = timestamp;
          }
          waveformAnimationRef.current = requestAnimationFrame(animate);
        }
      };

      if (isThisPlaying) {
        waveformAnimationRef.current = requestAnimationFrame(animate);
      }

      // Redraw on window resize
      const handleResize = () => {
        const newRect = container.getBoundingClientRect();
        canvas.width = newRect.width;
        canvas.height = 32;
        const newWidth = canvas.width;
        const newBarWidth = newWidth / barCount;
        
        // Recreate base canvas with new dimensions
        baseCanvas.width = newWidth;
        baseCanvas.height = height;
        
        // Recreate progress canvas with new dimensions
        progressCanvas.width = newWidth;
        progressCanvas.height = height;
        
        // Redraw base waveform
        baseCtx.clearRect(0, 0, newWidth, height);
        waveformData.forEach((value, index) => {
          const x = index * newBarWidth;
          const barHeight = value * (height * 0.8);
          baseCtx.fillStyle = unplayedColor;
          baseCtx.fillRect(x, centerY - barHeight / 2, newBarWidth - 1, barHeight);
        });
        
        // Redraw everything
        lastDrawnBarIndex = -1;
        const progressTime = isThisPlaying && audioRef.current ? audioRef.current.currentTime : 0;
        drawWaveformWithProgress(progressTime);
      };
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (waveformAnimationRef.current) {
          cancelAnimationFrame(waveformAnimationRef.current);
          waveformAnimationRef.current = null;
        }
      };
    }, [waveformData, playingAudio, url, isDarkMode, isThisPlaying]);
    
    return (
      <div 
        className={`flex flex-col gap-2 px-3 py-2 rounded-lg ${
          isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Timestamp Display */}
        <div className={`flex items-center justify-center text-xs font-mono ${
          isDarkMode ? 'text-blue-400' : 'text-blue-600'
        }`}>
          <span className="font-semibold">Timestamp: </span>
          <span className="ml-1">{actualTimestamp}</span>
        </div>

        {/* Controls Row */}
        <div className="flex items-center gap-3">
          {/* Skip Back */}
          <button
            onClick={() => skipTime(-5)}
            className={`flex-shrink-0 p-1 rounded hover:bg-opacity-50 ${
              isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'
            }`}
            title="Back 5s"
          >
            <SkipBack size={16} />
          </button>

          {/* Play/Pause */}
          <button
            onClick={() => handlePlayAudio(url, messageId)}
            className={`flex-shrink-0 p-2 rounded-full ${
              isDarkMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
          >
            {isThisPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>

          {/* Skip Forward */}
          <button
            onClick={() => skipTime(5)}
            className={`flex-shrink-0 p-1 rounded hover:bg-opacity-50 ${
              isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'
            }`}
            title="Forward 5s"
          >
            <SkipForward size={16} />
          </button>

          {/* Current Time */}
          <span className={`flex-shrink-0 text-xs font-mono ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {formatAudioTime(playbackTime)}
          </span>

          {/* Waveform and Progress Bar Container */}
          <div className="flex-grow flex flex-col gap-1">
            {/* Waveform Visualization */}
            {waveformData && waveformData.length > 0 && playingAudio === url && (
              <div 
                className="relative w-full h-8 cursor-pointer"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  const newTime = percent * (duration || 0);
                  audioRef.current.currentTime = newTime;
                  setCurrentTime(newTime);
                }}
              >
                <canvas
                  ref={localWaveformCanvasRef}
                  className="w-full h-full"
                  style={{ imageRendering: 'pixelated' }}
                />
              </div>
            )}
            
            {/* Progress Bar - Full width */}
            <div 
              className={`relative w-full h-2 rounded-full cursor-pointer ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-300'
              }`}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const percent = (e.clientX - rect.left) / rect.width;
                const newTime = percent * (duration || 0);
                audioRef.current.currentTime = newTime;
                setCurrentTime(newTime);
              }}
            >
              <div 
                className={`absolute left-0 top-0 h-full rounded-full ${
                  isDarkMode ? 'bg-blue-500' : 'bg-blue-600'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
              {/* Thumb indicator */}
              <div 
                className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow ${
                  isDarkMode ? 'bg-gray-200 border border-gray-500' : 'bg-white border border-gray-300'
                }`}
                style={{ left: `calc(${progressPercent}% - 6px)` }}
              />
            </div>
          </div>

          {/* Duration */}
          <span className={`flex-shrink-0 text-xs font-mono ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {formatAudioTime(playingAudio === url ? duration : 0)}
          </span>

          {/* Download Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (url) {
                downloadAudio(url, `audio_${messageId}.mp3`, messageId);
              } else {
                showToast("No audio available for download", "warning");
              }
            }}
            className={`flex-shrink-0 p-1 rounded hover:bg-opacity-50 ${
              isDarkMode ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700' : 'text-gray-500 hover:text-blue-600 hover:bg-gray-200'
            }`}
            title="Download Audio"
            disabled={!url}
          >
            <Download size={16} />
          </button>

          {/* Close Button */}
          <button
            onClick={() => togglePlayerExpand(messageId, url)}
            className={`flex-shrink-0 p-1 rounded hover:bg-opacity-50 ${
              isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
            }`}
            title="Close player"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  };



  const downloadAudio = async (url, filename, messageId) => {
  try {
    // If messageId is provided, fetch the formatted filename from the API
    // Format respects user's time format preference (12h or 24h)
    let downloadFilename = filename;
    if (messageId) {
      try {
        const res = await axios.get(`${edgeServerEndpoint}/audio_url/${messageId}?time_format=${timeFormat}`);
        // Use formatted filename from API response, fallback to provided filename
        downloadFilename = res.data.utc_filename || filename;
      } catch (err) {
        console.warn("Could not fetch formatted filename from API, using provided filename:", err);
        // Continue with the provided filename if API call fails
      }
    }
    
    const response = await fetch(url);
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = downloadFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    // Success notifications removed - only show errors
  } catch (err) {
    logger.error("Failed to download audio:", err);
    showToast("Failed to download audio", "error");
  }
};

  // Track loading state for re-transcribe per message
  const [retranscribeLoading, setRetranscribeLoading] = useState({});

  // Render
  return (
    <div
      className={`relative flex flex-col ${
        isDarkMode ? "bg-gray-900" : "bg-white"
      } ${isFullscreen ? "fixed inset-0 z-50 h-screen" : "flex-1 min-h-0"}`}
    >
      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className={`overflow-y-auto ${
          isDarkMode ? "dark-mode-scrollbar" : ""
        } ${
          isFullscreen
            ? "h-screen p-4 sm:p-6"
            : "flex-1 min-h-0 p-1 sm:p-2 md:p-4"
        }`}
      >
        <div
          className={`${
            isMobile ? "space-y-1 w-full" : "w-full space-y-2"
          }`}
        >
          <div ref={messagesTopRef} className="h-px" />
          {messages.map((item, index) => {
  if (hiddenMessages.has(item.id)) return null;

  // Check for regex and wildcard matches (for indicators) - only if hallucination filtering is enabled
  const { regex, wildcard } = isHallucinationEnabled 
    ? checkPatternMatches(item.message, hallucinations)
    : { regex: false, wildcard: false };
  
  // Replace hallucination words with "....." in the message text - only if enabled
  const filteredMessage = item.message !== 'No transcription available' 
    ? (isHallucinationEnabled 
        ? replaceHallucinationWords(item.message, hallucinations)
        : item.message)
    : item.message;

  // Calculate visible index for alternating rows (accounting for hidden messages)
  // Count visible messages before this one
  let visibleIndex = 0;
  for (let i = 0; i < index; i++) {
    if (!hiddenMessages.has(messages[i].id)) {
      visibleIndex++;
    }
  }
  const isEvenRow = visibleIndex % 2 === 0;

  return (
    <div
      key={item.id}
      className={`${isMobile ? 'flex flex-col' : 'flex items-start space-x-2'} ${isMobile ? 'mb-2 pb-2 border-b' : 'mb-2'} min-w-0 group hover:bg-opacity-80 transition-colors duration-150 rounded-lg ${isMobile ? 'px-2 py-2' : 'p-2'} ${
        isEvenRow
          ? isDarkMode
            ? 'bg-gray-800/30'
            : 'bg-gray-50'
          : isDarkMode
            ? 'bg-gray-800/50'
            : 'bg-white'
      } ${isDarkMode && isMobile ? 'border-gray-700' : isMobile ? 'border-gray-200' : ''}`}
    >
      {/* Mobile: Header row with timestamp, channel, and play button */}
      {isMobile ? (
        <>
          <div className="flex items-start justify-between mb-1">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              {/* Timestamp and Channel/Tag/Person container */}
              <div className="flex flex-col items-start space-y-1 min-w-0 flex-1">
                {/* Timestamp */}
                {showTime && (
                  <span className="text-gray-400 text-xs font-mono">
                    {formatTime(item.time, timezone)}
                  </span>
                )}
                {/* Channel / Tag / Person (mobile header) */}
                {((showCar && channels[item.channel]?.tag) || 
                  (showChannel && channels[item.channel]?.name) || 
                  (showPerson && channels[item.channel]?.person)) && (
                  <span
                    className={`text-xs font-semibold truncate px-2 py-0.5 rounded-full ${
                      isDarkMode ? "bg-gray-800 text-gray-100" : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {(() => {
                      const parts = [
                        showCar && channels[item.channel]?.tag,
                        showChannel && channels[item.channel]?.name,
                        showPerson && channels[item.channel]?.person
                          ? `(${channels[item.channel]?.person})`
                          : "",
                      ].filter(Boolean);

                      // Fallback to channel name if nothing is selected or available
                      if (parts.length === 0) {
                        return channels[item.channel]?.name || "Unknown";
                      }

                      return parts.join(" - ");
                    })()}
                  </span>
                )}
              </div>
            </div>
            {/* Play button for mobile */}
            {canPlayAudio && item.url && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlayerExpand(item.id, item.url);
                }}
                className={`p-2 rounded-full flex-shrink-0 ${
                  expandedPlayer === item.id
                    ? 'bg-blue-500 text-white'
                    : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {playingAudio === item.url && isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
            )}
          </div>
          {/* Transcription text - tappable to expand actions or open player */}
          <div 
            className={`text-sm leading-relaxed ${canPlayAudio && item.url ? 'cursor-pointer' : 'cursor-pointer'} ${
              isDarkMode ? 'text-gray-200' : 'text-gray-800'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              // If message has audio, open player; otherwise expand actions
              if (canPlayAudio && item.url) {
                togglePlayerExpand(item.id, item.url);
              } else {
                setExpandedMobileActions(expandedMobileActions === item.id ? null : item.id);
              }
            }}
          >
            {item.message !== 'No transcription available' ? (
              highlightText(filteredMessage, searchQuery)
            ) : item.status === 'queued' ? (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                isDarkMode ? 'bg-yellow-900/50 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
              }`}>
                queued
              </span>
            ) : (
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                isDarkMode ? 'bg-yellow-900/50 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
              }`}>
                .....
              </span>
            )}
            {regex && (
              <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                isDarkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-800'
              }`}>
                Hallucination
              </span>
            )}
            {/* Duration Card */}
            {item.url && item.message !== 'No transcription available' && (
              <DurationCard duration={item.duration} />
            )}
          </div>
          
          {/* Expanded mobile actions */}
          {expandedMobileActions === item.id && !isMultiSelectMode && (
            <div className={`mt-2 flex flex-wrap gap-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {canDeleteAudio && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteMessage(item.id);
                  }}
                  disabled={deletingIds.has(item.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${
                    isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {deletingIds.has(item.id) ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Delete
                </button>
              )}
            </div>
          )}
          
          {/* Audio Player for mobile - full width */}
          {expandedPlayer === item.id && item.url && canPlayAudio && (
            <div className="mt-2">
              <InlineAudioPlayer url={item.url} messageId={item.id} />
            </div>
          )}
        </>
      ) : (
        <>
          {/* Desktop layout - original */}
          {/* Timestamp and Channel/Tag/Person container */}
          <div className="flex-shrink-0 flex flex-col items-start space-y-1">
            {/* Channel/Tag/Person */}
            {((showCar && channels[item.channel]?.tag) || 
              (showChannel && channels[item.channel]?.name) || 
              (showPerson && channels[item.channel]?.person)) && (
              <span
                className={`font-semibold whitespace-nowrap px-2 py-0.5 rounded-full ${
                  isDarkMode ? "bg-gray-800 text-gray-100" : "bg-gray-100 text-gray-800"
                }`}
              >
                {[
                  showCar && channels[item.channel]?.tag,
                  showChannel && channels[item.channel]?.name,
                  showPerson && channels[item.channel]?.person ? `(${channels[item.channel]?.person})` : '',
                ]
                  .filter(Boolean)
                  .join(' - ')}
              </span>
            )}

            {/* Timestamp */}
            {showTime && (
              <span className="text-gray-400 text-xs font-mono px-2">
                {formatTime(item.time, timezone)}
              </span>
            )}
          </div>

          {/* Message & Tags + Inline Player */}
          <div className="flex-grow min-w-0">
        {/* Transcription */}
        <div 
          className={`break-words ${canPlayAudio && item.url ? 'cursor-pointer' : ''} ${isMobile ? 'text-sm' : ''} ${
            isDarkMode ? 'text-gray-200' : 'text-gray-800'
          }`}
          onClick={(e) => {
            if (canPlayAudio && item.url && !isMultiSelectMode) {
              e.stopPropagation();
              togglePlayerExpand(item.id, item.url);
            }
          }}
        >
          {item.message !== 'No transcription available' ? (
            highlightText(filteredMessage, searchQuery)
          ) : item.status === 'queued' ? (
            <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
              isDarkMode ? 'bg-yellow-900/50 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
            }`}>
              queued
            </span>
          ) : (
            <>
              <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                isDarkMode ? 'bg-yellow-900/50 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
              }`}>
                .....
              </span>
            </>
          )}

          {/* Hallucination Indicator */}
          {regex && (
            <div className="inline-flex ml-2">
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${
                  isDarkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-800'
                }`}
              >
                Hallucination
              </span>
            </div>
          )}

          {/* Duration Card */}
          {item.url && item.message !== 'No transcription available' && (
            <DurationCard duration={item.duration} />
          )}
        </div>

        {/* Audio Player - shows below transcription when expanded */}
        {expandedPlayer === item.id && item.url && canPlayAudio && (
          <div className="mt-2">
            <InlineAudioPlayer url={item.url} messageId={item.id} />
          </div>
        )}

        {/* Existing tags */}
        <div className="mt-1 flex items-center gap-2">
          {(tagsByMessage[item.id] || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {(tagsByMessage[item.id] || []).map((t) => (
                <span
                  key={t}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                    isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-800'
                  }`}
                >
                  {t}
                  {!isMultiSelectMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTagFromMessage(item.id, t);
                      }}
                      className="ml-1 text-red-500 hover:text-red-700"
                    >
                      <X size={12} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          
          {/* Tag refresh button */}
          {/* {!isMultiSelectMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                refreshTagsForMessage(item.id);
              }}
              className={`p-1 rounded-full transition-colors ${
                isDarkMode 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              title="Refresh tags"
              disabled={refreshingTags.has(item.id)}
            >
              {refreshingTags.has(item.id) ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <TagIcon size={14} />
              )}
            </button>
          )} */}
          
          {/* Loading indicator for tags */}
          {/* {(!checkedMessageIds.has(item.id) || refreshingTags.has(item.id)) && (
            <div className="flex items-center text-xs text-gray-500">
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
              {refreshingTags.has(item.id) ? 'Refreshing tags...' : 'Loading tags...'}
            </div>
          )} */}
        </div>

      </div>

      {/* Per-message actions */}
      {!isMultiSelectMode && (
        <div className={`flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'}`}>
          {/* Show re-transcribe button only for failed jobs (.....) as the left-most action */}
          {item.message === 'No transcription available' && item.status !== 'queued' && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (!item.id) return;
                setRetranscribeLoading((prev) => ({ ...prev, [item.id]: true }));
                showToast('Transcribing...', 'info');
                try {
                  const response = await axios.post(`${edgeServerEndpoint}/transcribe/${item.id}`);
                  if (response.data && response.data.transcription) {
                    setMessages((prev) => prev.map((m) => m.id === item.id ? { ...m, message: response.data.transcription } : m));
                    // Success notifications removed - only show errors
                  } else {
                    showToast('No transcription returned', 'warning');
                  }
                } catch (err) {
                  showToast('Transcription failed', 'error');
                } finally {
                  setRetranscribeLoading((prev) => ({ ...prev, [item.id]: false }));
                }
              }}
              className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-xs'} rounded-md font-semibold transition-colors ${isDarkMode ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-green-100 text-green-800 hover:bg-green-200'} ${isMobile ? 'mr-1' : 'mr-2'}`}
              title="Re-transcribe"
              disabled={!!retranscribeLoading[item.id]}
            >
              {retranscribeLoading[item.id] ? (
                <span className="flex items-center"><Loader2 className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} animate-spin ${isMobile ? 'mr-1' : 'mr-2'}`} />{isMobile ? 'Trans...' : 'Transcribing...'}</span>
              ) : (
                isMobile ? 'Re-trans' : 'Re-transcribe'
              )}
            </button>
          )}

          {canPlayAudio && item.url && (
            <AudioIcon url={item.url} messageId={item.id} />
          )}

         {canDeleteAudio && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteMessage(item.id);
              }}
              className={`p-2 ${
                isDarkMode ? 'text-gray-400 hover:text-red-500' : 'text-red-500 hover:text-red-700'
              }`}
              disabled={deletingIds.has(item.id)}
            >
              {deletingIds.has(item.id) ? (
                <Loader2 className="w-5 h-5" animate-spin />
              ) : (
                <Trash2 className="w-5 h-5" />
              )}
            </button>
          )}

        </div>
      )}
        </> 
      )}
    </div>
  );
})}
{/* Message count indicator - only show in continuous scrolling mode */}
          {inboxViewMode === 'continuous' && messages.length > 0 && (
            <div className={`sticky bottom-0 left-0 right-0 z-10 py-2 px-4 text-center text-xs font-medium ${
              isDarkMode 
                ? 'bg-gray-900/95 backdrop-blur-sm text-gray-300 border-t border-gray-700' 
                : 'bg-white/95 backdrop-blur-sm text-gray-600 border-t border-gray-200'
            }`}>
              <div className="flex items-center justify-center gap-2">
                {isLoadingMore && (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                )}
                <span>
                  Showing {messages.length} of {totalMessages} messages
                </span>
                {hasMoreMessages && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    isDarkMode 
                      ? 'bg-blue-900/50 text-blue-300' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {totalMessages - messages.length} more available
                  </span>
                )}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} className="h-px" />
        </div>
      </div>

      {/* Incident Report Modal */}
      {showIncidentModal && (
        <IncidentReportModal
          isOpen={showIncidentModal}
          selectedMessages={selectedMessages}
          messages={messages}
          formatTime={formatTime}
          timezone={timezone}
          timeFormat={timeFormat}
          onClose={() => setShowIncidentModal(false)}
          onSubmit={handleIncidentSubmit}
          isDarkMode={isDarkMode}
          tagsByMessage={tagsByMessage}
        />
      )}

      {/* Floating scroll-to-newest button for all devices */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className={`absolute bottom-6 right-4 z-50 flex items-center justify-center shadow-lg transition-all duration-300 ${
            newMessageCount > 0 
              ? 'rounded-full px-4 py-3' 
              : 'rounded-full w-12 h-12'
          } ${
            isDarkMode 
              ? 'bg-blue-600 hover:bg-blue-500 text-white' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
          style={{
            boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)'
          }}
          title={reverseSort ? "Scroll to newest messages (top)" : "Scroll to newest messages (bottom)"}
        >
          {newMessageCount > 0 ? (
            <div className="flex items-center gap-2">
              <MessageSquare size={18} />
              <span className="text-sm font-semibold">{newMessageCount}</span>
              {reverseSort ? <ArrowUp size={18} /> : <ArrowUp size={18} className="rotate-180" />}
            </div>
          ) : (
            reverseSort ? <ArrowUp size={22} /> : <ArrowUp size={22} className="rotate-180" />
          )}
        </button>
      )}

    </div>
  );
};

export default FullscreenMessages;